import Delete from '@mui/icons-material/DeleteOutline';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router';

import { AppCard } from '@/components/atoms';
import { type AccessoryOrderEntry } from '@/hooks/useAccessoryOrderItems';
import { accessoryPath } from '@/pages/accessories/accessoryModel';
import { AccessoryPhoto, AccessoryStockChip } from '@/pages/accessories/molecules';
import { formatUgx } from '@/utils/vehicleFormat';

import { AccessoryQuantityStepper } from './AccessoryQuantityStepper';

type Props = {
  entries: AccessoryOrderEntry[];
  onRemove: (slug: string) => void;
  onSetQuantity?: (slug: string, quantity: number) => void;
  readOnly?: boolean;
};

function leadTimeLabel(leadTime: { min: number; max: number } | null | undefined): string | null {
  if (!leadTime) return null;
  const { min, max } = leadTime;
  return max <= 14 ? `Ready in ${min}–${max} working days` : `Ready in ${Math.round(min / 7)}–${Math.round(max / 7)} weeks`;
}

/** The parts on the order list, re-read from the API so prices are current. */
export function AccessoryOrderTable({ entries, onRemove, onSetQuantity, readOnly = false }: Props) {
  return (
    <Stack component="ul" spacing={4} sx={{ listStyle: 'none', p: 0, m: 0 }}>
      {entries.map((entry) => {
        const a = entry.accessory;
        return (
          <Box component="li" key={entry.slug}>
            <AppCard sx={{ p: { xs: 3, sm: 4 }, opacity: entry.isGone ? 0.6 : 1 }}>
              <Stack direction="row" spacing={{ xs: 3, sm: 5 }} alignItems="flex-start">
                <Box
                  sx={{
                    width: { xs: 88, sm: 144 },
                    flexShrink: 0,
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: 'action.hover',
                  }}
                >
                  {entry.isPending ? (
                    <Skeleton variant="rectangular" sx={{ aspectRatio: '1 / 1', height: 'auto' }} />
                  ) : a ? (
                    <AccessoryPhoto image={a.cover} fallbackAlt={entry.name} category={a.category} sizes="144px" />
                  ) : null}
                </Box>

                <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
                    <Stack spacing={0.5} sx={{ minWidth: 0, alignItems: 'flex-start' }}>
                      <Typography variant="h6" component="h2">
                        {a ? (
                          <Box
                            component={RouterLink}
                            to={accessoryPath(a)}
                            sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'primary.main' } }}
                          >
                            {entry.name}
                          </Box>
                        ) : (
                          entry.name
                        )}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {a ? `${a.categoryLabel} · ${a.brand}` : '—'}
                      </Typography>
                    </Stack>
                    {!readOnly && (
                      <IconButton
                        size="small"
                        aria-label={`Remove ${entry.name} from your order list`}
                        onClick={() => onRemove(entry.slug)}
                        sx={{ color: 'text.secondary', mt: -1, mr: -1, '&:hover': { color: 'error.main' } }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>

                  {entry.isGone ? (
                    <Typography variant="body2" color="error.main">
                      This part is no longer listed. It will not be included in your request.
                    </Typography>
                  ) : (
                    a && (
                      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                        <AccessoryStockChip stock={a.stock} />
                        <Typography variant="body2" color="text.secondary">
                          {leadTimeLabel(a.leadTimeDays) ?? 'Lead time on request'}
                        </Typography>
                      </Stack>
                    )
                  )}

                  {a && !entry.isGone && (
                    <Stack
                      direction="row"
                      alignItems="flex-end"
                      justifyContent="space-between"
                      flexWrap="wrap"
                      useFlexGap
                      spacing={3}
                      sx={{ pt: 1 }}
                    >
                      {readOnly ? (
                        <Typography variant="body2" color="text.secondary">
                          Qty {entry.quantity}
                          {a.priceUgx !== null && ` × ${formatUgx(a.priceUgx)}`}
                        </Typography>
                      ) : (
                        <Stack spacing={0.5}>
                          <AccessoryQuantityStepper
                            name={entry.name}
                            quantity={entry.quantity}
                            onChange={(q) => onSetQuantity?.(entry.slug, q)}
                          />
                          {a.priceUgx !== null && (
                            <Typography variant="caption" color="text.secondary" className="tabular-nums">
                              {formatUgx(a.priceUgx)} each
                            </Typography>
                          )}
                        </Stack>
                      )}
                      <Typography className="tabular-nums" sx={{ fontWeight: 800, fontSize: '1.25rem' }}>
                        {a.priceUgx === null ? 'Price on request' : formatUgx(a.priceUgx * entry.quantity)}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Stack>
            </AppCard>
          </Box>
        );
      })}
    </Stack>
  );
}